import { TempIdToDatabaseIdMappingModel } from "client/Api/common";
import { DatabaseId, DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";
import {
	SpaceData,
	SpaceInfo,
	TileGroupSpacePreferenceData
} from "client/Api/Workspace/workspace";
import { isNil } from "client/Misc/utils";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { Guid } from "client_legacy/ndaw/guid";
import { ModifiedCollectionBuilder, setPropertiesPreservingRefWhenUnchanged } from "./common";

export function updateSpacesWithPersistedData(
	spaces: readonly SpaceData[],
	spaceId: number,
	tileGroupPreferences: readonly TileGroupSpacePreferenceData[],
	addedItemsIdsMap: TempIdToDatabaseIdMappingModel[]
): readonly SpaceData[] {
	const modifiedSpacesBuilder = new ModifiedCollectionBuilder<SpaceData>(spaces);

	const idMap: Map<Guid, number> = fromArray(
		addedItemsIdsMap,
		x => x.tempId,
		x => x.databaseId
	);

	for (let space of spaces) {
		if (space.id === spaceId) {
			space = {
				...space,
				tileGroupsPreferences: tileGroupPreferences
			};
		}

		space = setPropertiesPreservingRefWhenUnchanged(
			space,
			{
				tileGroupsPreferences: updateTileGroupPreferencesWithPersistedIds(
					space.tileGroupsPreferences,
					idMap
				)
			}
		);

		modifiedSpacesBuilder.push(space);
	}

	return modifiedSpacesBuilder.result();
}

export function addSpace(
	currentState: readonly SpaceData[],
	newSpace: SpaceInfo
): readonly SpaceData[] {
	const {
		id,
		kind,
		name,
		accessKind
	} = newSpace;

	return [
		...currentState,
		{
			id: id,
			kind: kind,
			name: name,
			tileGroupsPreferences: [],
			accessKind: accessKind
		}
	];
}

export function deleteSpace(
	currentState: readonly SpaceData[],
	spaceId: number
): readonly SpaceData[] {
	return currentState
		.filter(
			(x) => x.id !== spaceId
		);
}

export function updateSpace(
	currentState: readonly SpaceData[],
	updatedSpace: SpaceInfo
): readonly SpaceData[] {
	const modifiedSpacesBuilder = new ModifiedCollectionBuilder<SpaceData>(currentState);

	for (const space of currentState)
	{
		if (space.id === updatedSpace.id) {
			if (space.kind !== updatedSpace.kind) {
				throw new Error("Cannot change the kind of a space");
			}

			modifiedSpacesBuilder.push({
				id: space.id,
				kind: space.kind,
				tileGroupsPreferences: space.tileGroupsPreferences,
				name: updatedSpace.name,
				accessKind: updatedSpace.accessKind
			});
		} else {
			modifiedSpacesBuilder.push(space);
		}
	}

	return modifiedSpacesBuilder.result();
}

function updateTileGroupPreferencesWithPersistedIds(
	tileGroupPreferences: readonly TileGroupSpacePreferenceData[],
	addedItemsIdsMap: ReadonlyMap<Guid, number>
): readonly TileGroupSpacePreferenceData[] {
	const modifiedPreferencesBuilder = new ModifiedCollectionBuilder<TileGroupSpacePreferenceData>(tileGroupPreferences);

	for (const preference of tileGroupPreferences) {
		const updatedPreference: TileGroupSpacePreferenceData = updateTileGroupIdInPreference(
			preference,
			addedItemsIdsMap
		);

		modifiedPreferencesBuilder.push(updatedPreference);
	}

	return modifiedPreferencesBuilder.result();
}

function updateTileGroupIdInPreference(
	preference: TileGroupSpacePreferenceData,
	addedItemsIdsMap: ReadonlyMap<Guid, number>
): TileGroupSpacePreferenceData {
	const tileGroupId: DatabaseOrTempObjectId = preference.tileGroupId;

	if (tileGroupId.hasDatabaseId) {
		return preference;
	}

	const databaseId: number | undefined = addedItemsIdsMap.get(tileGroupId.tempId);

	if (isNil(databaseId)) {
		console.error("Cannot find id map for newly created tile group");
		return preference;
	}

	return {
		...preference,
		tileGroupId: new DatabaseId(databaseId)
	};
}
